import {
  getItemTotalWidths,
  splitItemsByTheirPriority,
} from '@components/components/Navigation/components/DesktopNavigation/components/MainNavigation/utils/utils';
import { useEffect, useRef, useState } from 'react';
import { useMenuStore } from '@ariakit/react';

const useMainNavigation = (mainMenu, isReduced) => {
  const [widthOfVariants, setWidthOfVariants] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [regularItems, setRegularItems] = useState([]);
  const [moreItems, setMoreItems] = useState([]);
  // when search open and not all items fit anymore, this will become true
  const [displayOnlyActiveItem, setDisplayOnlyActiveItem] = useState(false);

  const navRef = useRef(null);
  const logoRef = useRef(null);
  const itemsRef = useRef([]);
  const moreRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState();
  const menu = useMenuStore({ open, setOpen });
  const [isLoading, setIsLoading] = useState(true);

  const handleSearchOpen = openSearch => {
    if (openSearch) {
      // total width of navbar minus size of open search bar (300px) minus buffer for left and right side
      const availableSpace = navRef.current.clientWidth - 300 - 36;
      // check if available space enough to fit all items (including open search bar)
      if (availableSpace < widthOfVariants[0]) {
        setDisplayOnlyActiveItem(true);
      }
    } else {
      setDisplayOnlyActiveItem(false);
    }

    setIsSearchOpen(openSearch);
  };

  const checkIfOverflowing = widthOfVariantsDefault => {
    const widthOfVariantsSelected = widthOfVariants.length
      ? widthOfVariants
      : widthOfVariantsDefault;

    if (!widthOfVariantsSelected) {
      return;
    }

    setIsSearchOpen(false);
    const searchBarWidth = searchRef.current?.clientWidth || 0;
    const navBarWidth = navRef.current?.clientWidth || 0;
    // width of entire navbar minus width of search bar minus buffer for left
    // and right side;
    const availableSpace = navBarWidth - searchBarWidth - 36;

    // depending on whether the navigation is reduced or not, different
    // options for the priority navigation are available

    // if available space cannot fit all items, keep two highest priorities
    // and put rest to `more`
    if (
      availableSpace < widthOfVariantsSelected[0] &&
      availableSpace > widthOfVariantsSelected[1]
    ) {
      const [highestPriorities, lowestPriorities] = splitItemsByTheirPriority(
        mainMenu,
        2,
        isReduced,
      );

      setRegularItems(highestPriorities);
      setMoreItems(lowestPriorities);

      // if available space cannot fit all items, keep the highest priority
      // and put rest to `more`
    } else if (availableSpace < widthOfVariantsSelected[1]) {
      const [highestPriorities, lowestPriorities] = splitItemsByTheirPriority(
        mainMenu,
        1,
        isReduced,
      );

      setRegularItems(highestPriorities);
      setMoreItems(lowestPriorities);
    }
    // if available space fits all items, display all and show no `more` menu item
    if (availableSpace > widthOfVariantsSelected[0]) {
      setRegularItems(mainMenu);
      setMoreItems([]);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      checkIfOverflowing();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [widthOfVariants]);

  useEffect(() => {
    setIsLoading(true);
    setRegularItems(mainMenu);
    // This is a workaround to get the width of the more menu item.
    setMoreItems([1]);
  }, [mainMenu]);

  useEffect(() => {
    if (isLoading && moreItems.includes(1)) {
      const widthOfVariantsDefault = getItemTotalWidths(
        mainMenu,
        logoRef,
        itemsRef,
        moreRef,
        isReduced,
      );
      setWidthOfVariants(widthOfVariantsDefault);
      checkIfOverflowing(widthOfVariantsDefault);
      setIsLoading(false);
    }
  }, [isLoading, moreItems]);

  return {
    navRef,
    logoRef,
    itemsRef,
    searchRef,
    moreRef,
    regularItems,
    moreItems,
    menu,
    isLoading,
    isSearchOpen,
    handleSearchOpen,
    displayOnlyActiveItem,
    setDisplayOnlyActiveItem,
  };
};
export default useMainNavigation;
